import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Collapse,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircleOutlineOutlined';
import { ShopOnboardingID, ShopOnboardingSteps } from '@prisma/client';
import useGetOnboarding from '../../../hooks/businesses/useGetOnboarding';
import Progress from '../../global/ProgressBar';
import { PAGE_PADDING } from '../../../constants/ui';
import { ONBOARDING_STEPS_ROUTES } from '../../../constants/businesses';
import useCompleteOnboardingStep from '../../../hooks/businesses/useCompleteOnboardingStep';

interface Props {
  step: ShopOnboardingID | null;
  sx?: SxProps<Theme>;
  completeOnRender?: boolean;
}

function OnboardingBanner({ step: stepId, sx, completeOnRender }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    steps,
    isEnabled,
    isOnboardingComplete,
    categorySteps,
    completedSteps,
    incompleteSteps,
    percentCompleted,
    isLoading,
  } = useGetOnboarding();
  const { mutate: completeOnboardingStep } = useCompleteOnboardingStep();
  const hasCompletedRef = useRef(false);

  const step = steps.find(s => s.id === stepId);
  const isCompleted = completedSteps?.some(
    completedStep => completedStep.id === stepId,
  );
  const [initialIsComplete] = useState(isCompleted);
  const [initialOnboardingComplete] = useState(isOnboardingComplete);
  const isVisible = !!step && (!isCompleted || !initialIsComplete);
  const showCompleteCheck = isCompleted && !initialIsComplete;

  const getNextStep = useCallback(() => {
    const allSteps = Object.values(categorySteps ?? {})
      ?.reduce(
        (acc, thisCategorysteps) => [...acc, ...thisCategorysteps],
        [] as ShopOnboardingSteps[],
      )
      ?.filter(
        thisStep =>
          incompleteSteps?.some(
            incompleteStep => incompleteStep.id === thisStep.id,
          ),
      );
    if (!allSteps?.length) return null;

    const currentStepIndex = allSteps.findIndex(
      thisStep => thisStep.id === stepId,
    );

    const next = allSteps[currentStepIndex + 1] || allSteps.find(Boolean);
    if (!next || next.id === step?.id) return null;
    return next;
  }, [categorySteps, incompleteSteps, step?.id, stepId]);

  const nextStep = useMemo(getNextStep, [getNextStep]);

  const onNextStep = useCallback(() => {
    if (!nextStep) return;

    const route = ONBOARDING_STEPS_ROUTES[nextStep.id];
    if (!route) return;

    navigate(route);
  }, [navigate, nextStep]);

  useEffect(() => {
    if (
      completeOnRender &&
      stepId &&
      !isCompleted &&
      isEnabled &&
      !hasCompletedRef.current
    ) {
      hasCompletedRef.current = true;
      completeOnboardingStep(stepId);
    }
  }, [
    completeOnRender,
    stepId,
    completeOnboardingStep,
    isCompleted,
    isEnabled,
  ]);

  if (
    !isEnabled ||
    isLoading ||
    (isOnboardingComplete && initialOnboardingComplete)
  )
    return null;

  return (
    <Collapse orientation="vertical" in={isVisible}>
      <Box
        boxSizing="border-box"
        overflow="hidden"
        width="100%"
        py={{ xs: 2, sm: 1 }}
        px={PAGE_PADDING}
        bgcolor={theme.palette.lightGrey.main}
        display="flex"
        flexDirection={{
          xs: 'column',
          sm: 'row',
        }}
        alignItems={{
          xs: 'flex-start',
          sm: 'center',
        }}
        justifyContent="space-between"
        gap={{
          xs: 0,
          sm: 1,
        }}
        sx={sx}>
        {isVisible && (
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="h5" component="p" color="text.secondary">
              {step?.name || '-'}
            </Typography>
            {showCompleteCheck && (
              <CheckCircle
                width="14px"
                htmlColor={theme.palette.secondary.main}
                sx={{
                  width: '14px',
                  height: '14px',
                }}
              />
            )}
          </Box>
        )}
        <Box
          flex={1}
          width={{
            xs: '100%',
            sm: 'auto',
          }}
          ml={{ xs: 0, sm: 'auto' }}
          maxWidth={{
            xs: '100%',
            sm: '50%',
            lg: '33%',
          }}
          display="flex"
          alignItems="center"
          gap={{
            xs: 1,
            sm: 2.5,
          }}
          minHeight={theme.spacing(5)}>
          <Progress progress={percentCompleted} includePercentage />
          {!!nextStep && (
            <Button
              onClick={onNextStep}
              variant="light"
              sx={{
                whiteSpace: 'nowrap',
                bgcolor: theme.palette.background.paper,
                px: 4,
              }}>
              Next step
            </Button>
          )}
        </Box>
      </Box>
    </Collapse>
  );
}

export default OnboardingBanner;
